import { evaluateAt } from './evaluate';
import { type Node } from './parse';

/**
 * Where an expression is zero.
 *
 * ## Why a module of its own
 *
 * Because two features need the same answer and must not disagree about it. The bảng biến
 * thiên finds the stationary points of `f` by solving `f' = 0`; marking the extrema on the
 * curve solves exactly the same equation. If the two carried separate solvers, a teacher could
 * put a table beside a graph and find the table announcing a maximum the graph has not marked —
 * and the figure and the table are meant to be two readings of one function.
 *
 * The code here is the solver `variation.ts` grew, lifted out unchanged rather than rewritten:
 * it is the part of that file that had nothing to do with tables.
 *
 * ## Two kinds of root, and why the second is the one that matters
 *
 * A sign-change search finds where `3x² − 3` **crosses** zero, and that is `x³ − 3x` handled.
 * It finds nothing at all for `3x²`, which merely **touches** zero — so `y = x³` would come
 * back with no stationary point, when the flat spot at the origin is precisely what a student
 * is being asked about. Both are looked for.
 */

/** Halvings spent locating a root. Sixty is float precision on any range. */
const BISECTIONS = 60;

/**
 * Rounds a computed root to the round number it is trying to be.
 *
 * Bisection converges to `0.9999999999999998`, and a figure that says the maximum is at
 * `1.0000` when the answer is `1` reads as a machine's output rather than as mathematics. The
 * snap is deliberately tight — a millionth — so a root that genuinely is not round keeps its
 * value.
 */
export function snapNumber(x: number): number {
  const rounded = Math.round(x);
  // `Math.round(-1e-20)` is `-0`, which prints as `-0` and compares unequal to `0` under
  // `Object.is`. A point at negative zero is a point at zero.
  if (Math.abs(x - rounded) < 1e-7) return rounded === 0 ? 0 : rounded;

  const halves = Math.round(x * 2) / 2;
  if (Math.abs(x - halves) < 1e-9) return halves;

  return Math.round(x * 1e9) / 1e9;
}

/** A magnitude the function reaches ordinarily, used to tell a pole from a steep stretch. */
export function typicalMagnitude(values: readonly (number | null)[]): number {
  const magnitudes = values.filter((value): value is number => value !== null).map(Math.abs);
  if (magnitudes.length === 0) return 1;

  const sorted = [...magnitudes].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 1;
  return Math.max(median, 1);
}

/**
 * The zeros of `g` among a list of sample points, in increasing order.
 *
 * The samples are passed in rather than computed, because the two callers have already built a
 * grid and drawing a second one would move the answers: a touching root is found by the shape
 * of three consecutive samples, so a different spacing gives a slightly different point. One
 * grid, one answer.
 */
export function rootsAmong(g: Node, xs: readonly number[]): readonly number[] {
  if (xs.length < 3) return [];

  const values = xs.map((x) => evaluateAt(g, x));
  const scale = typicalMagnitude(values);
  const found: number[] = [];

  for (let index = 1; index < xs.length; index++) {
    const before = values[index - 1] ?? null;
    const here = values[index] ?? null;
    const left = xs[index - 1];
    const right = xs[index];
    if (before === null || here === null || left === undefined || right === undefined) continue;

    if (before === 0) found.push(snapNumber(left));
    else if (before * here < 0) found.push(rootBetween(g, left, right));
  }

  /*
   * A touching root is a local minimum of |g| that reaches zero. The tolerance is relative to
   * what `g` does elsewhere, because "close to zero" is meaningless on its own: `3x^2` over
   * [−50, 50] runs to seven thousand, and `0.001` is not a root of `x^2 + 0.001` however small
   * it looks.
   */
  for (let index = 1; index < xs.length - 1; index++) {
    const before = values[index - 1] ?? null;
    const here = values[index] ?? null;
    const after = values[index + 1] ?? null;
    const left = xs[index - 1];
    const right = xs[index + 1];
    if (before === null || here === null || after === null) continue;
    if (left === undefined || right === undefined) continue;
    if (here === 0) continue;
    if (!(Math.abs(here) < Math.abs(before) && Math.abs(here) <= Math.abs(after))) continue;

    const at = minimiseAbs(g, left, right);
    const value = evaluateAt(g, at);
    if (value !== null && Math.abs(value) < scale * 1e-9) found.push(snapNumber(at));
  }

  return found;
}

function rootBetween(g: Node, left: number, right: number): number {
  let a = left;
  let b = right;
  const sign = Math.sign(evaluateAt(g, a) ?? 0);

  for (let i = 0; i < BISECTIONS; i++) {
    const middle = (a + b) / 2;
    const value = evaluateAt(g, middle);
    if (value === null) break;
    if (Math.sign(value) === sign) a = middle;
    else b = middle;
  }
  return snapNumber((a + b) / 2);
}

/** Golden-section search for the smallest |g| in a bracket. */
function minimiseAbs(g: Node, left: number, right: number): number {
  const ratio = (Math.sqrt(5) - 1) / 2;
  let a = left;
  let b = right;

  for (let i = 0; i < 80; i++) {
    const c = b - (b - a) * ratio;
    const d = a + (b - a) * ratio;
    const fc = Math.abs(evaluateAt(g, c) ?? Number.POSITIVE_INFINITY);
    const fd = Math.abs(evaluateAt(g, d) ?? Number.POSITIVE_INFINITY);
    if (fc < fd) b = d;
    else a = c;
  }
  return (a + b) / 2;
}
